// apps/admin/src/features/users/AdminUserCustomRolesSection.tsx
//
// Assign / revoke admin-scope CUSTOM roles for a user. Built-in roles are
// managed elsewhere (primary role); this surface only handles custom roles
// (Role.code pushed into User.roles[]). Shipping this unblocks UAT #1064/#1065
// (a custom role can now hold members → the roles-list delete guard is
// reachable).

import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { listAdminRoles, type AdminRoleRow } from '../../api/admin-roles.api.js';
import {
  grantAdminUserRole,
  revokeAdminUserRole,
} from '../../api/admin-users.api.js';

interface AdminUserCustomRolesSectionProps {
  userId: string;
  /** Every non-primary role code currently on the user (`additional_roles`). */
  assignedCodes: ReadonlyArray<string>;
  /** Called after a successful grant/revoke so the parent can refetch detail. */
  onChanged: () => void;
}

export function AdminUserCustomRolesSection({
  userId,
  assignedCodes,
  onChanged,
}: AdminUserCustomRolesSectionProps): JSX.Element {
  const { t } = useTranslation('users');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string>('');

  const rolesQuery = useQuery<AdminRoleRow[]>({
    queryKey: ['admin', 'roles', 'custom'],
    queryFn: () => listAdminRoles('custom'),
    staleTime: 30_000,
  });

  const customRoles = useMemo(
    () => (rolesQuery.data ?? []).filter((r) => !r.is_built_in),
    [rolesQuery.data],
  );
  const assigned = useMemo(
    () => customRoles.filter((r) => assignedCodes.includes(r.code)),
    [customRoles, assignedCodes],
  );
  const assignable = useMemo(
    () => customRoles.filter((r) => !assignedCodes.includes(r.code)),
    [customRoles, assignedCodes],
  );

  function afterChange(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    onChanged();
  }

  const grant = useMutation({
    mutationFn: (code: string) => grantAdminUserRole(userId, code),
    onSuccess: () => {
      setPicked('');
      toast({ title: t('customRoles.granted'), variant: 'success' });
      afterChange();
    },
    onError: () =>
      toast({ title: t('customRoles.grantError'), variant: 'error' }),
  });

  const revoke = useMutation({
    mutationFn: (code: string) => revokeAdminUserRole(userId, code),
    onSuccess: () => {
      toast({ title: t('customRoles.revoked'), variant: 'success' });
      afterChange();
    },
    onError: () =>
      toast({ title: t('customRoles.revokeError'), variant: 'error' }),
  });

  const busy = grant.isPending || revoke.isPending;

  return (
    <div
      className="rounded-md border border-pw-border bg-pw-bg p-4"
      data-testid="admin-user-custom-roles"
    >
      <h3 className="text-sm font-semibold text-pw-text">
        {t('customRoles.title')}
      </h3>
      <p className="mt-0.5 text-xs text-pw-text-muted">
        {t('customRoles.subtitle')}
      </p>

      <div className="mt-3 flex flex-wrap gap-2">
        {assigned.length === 0 ? (
          <span className="text-sm text-pw-text-muted">
            {t('customRoles.empty')}
          </span>
        ) : (
          assigned.map((r) => (
            <span
              key={r.code}
              className="inline-flex items-center gap-1 rounded-full border border-pw-border bg-pw-surface px-2 py-0.5 text-xs text-pw-text"
              data-testid={`admin-user-role-chip-${r.code}`}
            >
              {r.name}
              <button
                type="button"
                disabled={busy}
                onClick={() => revoke.mutate(r.code)}
                className="ml-0.5 text-pw-text-muted hover:text-pw-text disabled:opacity-50"
                aria-label={t('customRoles.removeAria', { role: r.name })}
              >
                ×
              </button>
            </span>
          ))
        )}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <Select value={picked} onValueChange={setPicked} disabled={busy}>
          <SelectTrigger
            className="w-64"
            data-testid="admin-user-role-picker"
          >
            <SelectValue placeholder={t('customRoles.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {assignable.map((r) => (
              <SelectItem key={r.code} value={r.code}>
                {r.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="default"
          size="sm"
          disabled={busy || picked === ''}
          onClick={() => picked && grant.mutate(picked)}
          data-testid="admin-user-role-grant"
        >
          {t('customRoles.grantBtn')}
        </Button>
      </div>
    </div>
  );
}

export default AdminUserCustomRolesSection;
